import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";
import { useTheme } from "@/context/ThemeContext";
import { LayoutDashboard, Users, Send, LogOut, Sparkles, FileText, Shield, Workflow, Globe, ShieldOff, CreditCard, Layers, Settings, Moon, Sun } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { config } = useConfig();
  const { resolved, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const appName = config.app_name || "Mailwave";

  const navItems = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/audiences", label: "Audiences", icon: Users },
    { to: "/segments", label: "Segments", icon: Layers },
    { to: "/campaigns", label: "Campaigns", icon: Send },
    { to: "/automations", label: "Automations", icon: Workflow },
    { to: "/templates", label: "Templates", icon: FileText },
    { to: "/domains", label: "Domains", icon: Globe },
    { to: "/settings", label: "Settings", icon: Settings },
    { to: "/suppressions", label: "Suppression", icon: ShieldOff },
    ...(config.features?.stripe_billing ? [{ to: "/billing", label: "Billing", icon: CreditCard }] : []),
  ];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen flex bg-white dark:bg-neutral-950">
      {user?.is_admin && config.features?.license_check && config.license && !config.license.valid && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-red-600 text-white text-sm px-4 py-2 text-center">
          {config.license.message || "License invalid — sending and user creation are blocked."}
        </div>
      )}
      {user?.is_admin && config.features?.license_check && config.license?.in_grace && config.license?.valid && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-white text-sm px-4 py-2 text-center">
          {config.license.message || "License grace period — renew your license soon."}
        </div>
      )}
      <aside data-testid="app-sidebar" className="w-64 shrink-0 border-r border-neutral-200 dark:border-neutral-800 flex flex-col" style={{ backgroundColor: "var(--bg-sidebar)" }}>
        <div className="h-16 flex items-center px-6 border-b border-neutral-200 dark:border-neutral-800">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-sm flex items-center justify-center" style={{ backgroundColor: "var(--brand-primary)" }}>
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <span className="font-display font-black tracking-tight text-lg text-neutral-900 dark:text-neutral-50">{appName}</span>
          </div>
        </div>

        <nav className="flex-1 px-3 py-6 space-y-1">
          <div className="label-overline px-3 mb-3">Workspace</div>
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900"
                      : "text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-100"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            );
          })}

          {user?.is_admin && (
            <>
              <div className="label-overline px-3 mt-6 mb-3">Admin</div>
              <NavLink
                to="/admin"
                data-testid="nav-admin"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900"
                      : "text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-100"
                  }`
                }
              >
                <Shield className="h-4 w-4" />
                Admin console
              </NavLink>
            </>
          )}
        </nav>

        <div className="border-t border-neutral-200 dark:border-neutral-800 p-3 space-y-1">
          <button
            type="button"
            onClick={toggleTheme}
            className="w-full flex items-center gap-3 px-2 py-2 rounded-sm text-sm text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
            data-testid="theme-toggle"
          >
            {resolved === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            {resolved === "dark" ? "Light mode" : "Dark mode"}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" className="w-full flex items-center gap-3 px-2 py-2 rounded-sm hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors">
                <div className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: "var(--brand-primary)" }}>
                  {initials}
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <div className="text-sm font-semibold truncate flex items-center gap-1">
                    {user?.name}
                    {user?.is_admin && <Shield className="h-3 w-3" style={{ color: "var(--brand-primary)" }} />}
                  </div>
                  <div className="text-xs text-neutral-500 truncate">{user?.email}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>{user?.company || "Personal"}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="settings-menu-item">
                <Settings className="h-4 w-4 mr-2" /> Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                <LogOut className="h-4 w-4 mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
